/**
 * Turn an enum-style token (snake_case) into a human label, e.g.
 * `series_c_growth` -> `Series C Growth`. Pure; used for read-only display of
 * backend enum values that have no design-provided label.
 */
export function humanizeToken(value: string): string {
  return value
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * Look up a backend enum value's display label, falling back to a humanized
 * form of the token itself when the map has no entry for it. Pure.
 *
 * The fallback matters for a read-only admin surface: a value added to a
 * backend enum before the frontend's label map catches up must still render as
 * `Series C Growth`, never as the raw `series_c_growth` sitting next to
 * properly-labelled fields.
 */
export function enumLabel(labels: Record<string, string>, value: string): string {
  // An own-property check, not a plain lookup: an enum value that collides
  // with an inherited Object property (`toString`, `constructor`) would
  // otherwise resolve to a function and render as source code.
  return Object.prototype.hasOwnProperty.call(labels, value)
    ? labels[value]
    : humanizeToken(value);
}

/** Join truthy parts with a separator, dropping blanks/null/undefined. */
export function joinTruthy(
  parts: Array<string | null | undefined>,
  separator = ', ',
): string {
  return parts.map((part) => part?.trim()).filter(Boolean).join(separator);
}

/**
 * A URL as people read it: no scheme, no `www.`, no trailing slash, e.g.
 * `https://www.acme.example/` -> `acme.example`. Pure.
 */
export function displayUrl(url: string): string {
  return url.replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/+$/, '');
}

/**
 * Format a number as a whole-dollar amount with thousands separators, e.g.
 * `2100000` -> `$2,100,000`. Returns undefined for null/undefined so callers
 * can omit the field entirely. Pure.
 */
export function formatCurrency(value: number | null | undefined): string | undefined {
  if (value == null) return undefined;
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

const COMPACT_USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/**
 * Format whole dollars in short form for a headline number, e.g. `480000` ->
 * `$480K`, `1250000` -> `$1.25M`. Undefined for null/undefined. Pure.
 */
export function formatCompactCurrency(value: number | null | undefined): string | undefined {
  if (value == null) return undefined;
  return COMPACT_USD.format(value);
}

/**
 * A low/high pair of whole dollars as one short range, e.g. `$2M - $3M`; a
 * single side alone is shown as-is, neither gives undefined. Pure.
 */
export function formatCompactCurrencyRange(
  low: number | null | undefined,
  high: number | null | undefined,
): string | undefined {
  const lo = formatCompactCurrency(low);
  const hi = formatCompactCurrency(high);
  if (lo && hi) return lo === hi ? lo : `${lo} - ${hi}`;
  return lo ?? hi;
}

/**
 * Format integer cents as a currency amount, e.g. `6900` -> `$69.00`.
 *
 * Separate from `formatCurrency`, which takes whole dollars and is used for
 * raise amounts and valuations. Every billing figure the API returns is
 * INTEGER cents, so this is the one place the division by 100 happens - a
 * component that does it inline is the bug this exists to prevent. Pure.
 *
 * Whole amounts keep their `.00` rather than dropping it: a price list where
 * one row reads `$69` and the next `$69.50` looks like a rendering fault.
 */
export function formatCents(value: number | null | undefined): string | undefined {
  if (value == null) return undefined;
  return `$${(value / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** An option list entry, as every `SelectOption` constant is shaped. */
interface LabelledOption {
  value: string;
  label: string;
}

/**
 * Display label of the option whose value matches, or undefined when the value
 * is blank or not in the list - so a read-only view can show its own empty
 * marker. Pure.
 */
export function optionLabel(
  options: readonly LabelledOption[],
  value: string | null | undefined,
): string | undefined {
  return value ? options.find((option) => option.value === value)?.label : undefined;
}

/** Comma-joined labels of every matching option, or undefined when none match. Pure. */
export function optionLabels(
  options: readonly LabelledOption[],
  values: readonly string[] | null | undefined,
): string | undefined {
  return joinTruthy((values ?? []).map((value) => optionLabel(options, value))) || undefined;
}

/**
 * A label with an optional parenthesised detail, e.g. `Other (Space mining)`.
 * Returns the label alone when there is no detail, and undefined without a label. Pure.
 */
export function withDetail(
  label: string | null | undefined,
  detail: string | null | undefined,
): string | undefined {
  if (!label) return undefined;
  const trimmed = detail?.trim();
  return trimmed ? `${label} (${trimmed})` : label;
}
